import type { DiagramProps } from '@/lib/scad-feature-types';
import { D } from './DiagramStyles';

export function SphereDiagram({ values }: DiagramProps) {
  const r = Math.max(1, values.r ?? 5);
  const fn = Math.max(3, values.fn ?? 30);

  const cx = 110, cy = 75;
  // Scale radius to SVG: max display radius 60px
  const displayR = Math.min(60, Math.max(14, (r / 20) * 55));

  // Equator ellipse rx/ry
  const eRx = displayR;
  const eRy = displayR * 0.28;

  // Points for $fn arc annotation (small arc near bottom-right)
  const segAngle = (2 * Math.PI) / fn;
  const arcStartAngle = -Math.PI / 6;
  const arcEndAngle = arcStartAngle + segAngle * 2;
  const ax1 = cx + displayR * Math.cos(arcStartAngle);
  const ay1 = cy + displayR * Math.sin(arcStartAngle);
  const ax2 = cx + displayR * Math.cos(arcEndAngle);
  const ay2 = cy + displayR * Math.sin(arcEndAngle);
  const largeArc = segAngle * 2 > Math.PI ? 1 : 0;

  return (
    <svg viewBox="0 0 280 150" width="100%" style={{ display: 'block' }} aria-label="Sphere diagram">
      <defs>
        <marker id="arr-s" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
          <path d="M0,1 L5,3 L0,5 Z" fill={D.DIM_STROKE} />
        </marker>
        <marker id="arr-s-rev" markerWidth="6" markerHeight="6" refX="1" refY="3" orient="auto">
          <path d="M5,1 L0,3 L5,5 Z" fill={D.DIM_STROKE} />
        </marker>
        <clipPath id="sphere-clip">
          <circle cx={cx} cy={cy} r={displayR} />
        </clipPath>
      </defs>

      {/* Main circle */}
      <circle cx={cx} cy={cy} r={displayR} fill={D.GEO_FILL} stroke={D.GEO_STROKE} strokeWidth="1.5" />

      {/* Equator ellipse */}
      <ellipse cx={cx} cy={cy} rx={eRx} ry={eRy}
               fill="none" stroke={D.GEO_STROKE} strokeWidth="1" strokeDasharray="4 3" opacity="0.5"
               clipPath="url(#sphere-clip)" />

      {/* Longitude arcs (2D suggestions of depth) */}
      <ellipse cx={cx} cy={cy} rx={eRy * 1.2} ry={displayR}
               fill="none" stroke={D.GEO_STROKE} strokeWidth="1" strokeDasharray="4 3" opacity="0.3" />
      <ellipse cx={cx} cy={cy} rx={eRy * 0.5} ry={displayR}
               fill="none" stroke={D.GEO_STROKE} strokeWidth="1" strokeDasharray="4 3" opacity="0.2" />

      {/* Radius dimension line */}
      <line x1={cx} y1={cy} x2={cx + displayR} y2={cy}
            stroke={D.DIM_STROKE} strokeWidth="1" strokeDasharray={D.DIM_DASH}
            markerEnd="url(#arr-s)" />
      <text x={cx + displayR / 2} y={cy - 6} fontSize="10" fontFamily="monospace"
            fill={D.DIM_STROKE} textAnchor="middle" opacity="0.9">r = {r}</text>

      {/* Center dot */}
      <circle cx={cx} cy={cy} r="2.5" fill={D.ORIGIN_COLOR} />

      {/* $fn annotation arc */}
      <path d={`M${ax1},${ay1} A${displayR},${displayR} 0 ${largeArc},1 ${ax2},${ay2}`}
            fill="none" stroke={D.X_COLOR} strokeWidth="2" opacity="0.8" />
      <text x={cx + displayR + 16} y={cy - displayR * 0.05}
            fontSize="9" fontFamily="monospace" fill={D.X_COLOR} opacity="0.85" textAnchor="start">
        $fn={fn}
      </text>
      <line x1={cx + displayR * 0.7} y1={cy + displayR * 0.15}
            x2={cx + displayR + 14} y2={cy}
            stroke={D.X_COLOR} strokeWidth="0.8" strokeDasharray="2 2" opacity="0.5" />

      {/* Info panel */}
      <text x="210" y="110" fontSize="9" fontFamily="monospace" fill="currentColor" opacity="0.4" textAnchor="start">r = radius</text>
      <text x="210" y="122" fontSize="9" fontFamily="monospace" fill="currentColor" opacity="0.4" textAnchor="start">$fn = segments</text>
    </svg>
  );
}
